import { Component, inject, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { HeaderComponent } from '../../components/header/header.component';
import { Appointment } from '../../models/appointment';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import TYPES from '../../constants/types';
import { MatIcon } from '@angular/material/icon';
import { DecimalPipe } from '@angular/common';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { AppointmentService } from '../../services/appointment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { getLocalHourKey } from '../../util/appointment-date.util';

@Component({
  selector: 'app-view-day',
  imports: [
    HeaderComponent,
    TranslatePipe,
    MatIcon,
    DecimalPipe
  ],
  templateUrl: './view-day.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './view-day.component.scss',
})
export class ViewDayComponent {
  authService: AuthService = inject(AUTH_SERVICE);
  appointmentService: AppointmentService<Appointment> = inject(AppointmentService<Appointment>);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);
  
  appointments: WritableSignal<Map<string,Appointment[]>> = signal<Map<string,Appointment[]>>(new Map());
  date: WritableSignal<string> = signal<string>('');

  STATUS:any;
  PROFILES:any;

  constructor(private router: Router){
    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.STATUS = TYPES.DATABASE.STATUS;

    const navigation = this.router.currentNavigation();
    const allAppointments = navigation?.extras.state?.['appointments'] ?? [];
    this.date.set(navigation?.extras.state?.['date'] ?? '');
    let aux: Map<string, Appointment[]> = new Map();
    for(let appointment of allAppointments){
      if(appointment.date){
        const key = getLocalHourKey(appointment.date);
        if(aux.has(key)){
          aux.get(key)?.push(appointment);
        }else{
          aux.set(key, [appointment]);
        }
      }
    }
    this.appointments.set(aux);
  }

  back(){
    history.back();
  }

  getAppointmentLocalStartEndTime(appointment: Appointment){
    let startTime = '';
    let endTime = '';
    if(appointment.date){
      const date = new Date(appointment.date);

      startTime = date.getHours().toString().padStart(2,'0')
        +':'
        +date.getMinutes().toString().padStart(2,'0');
      if(appointment.duration){
        const endTimeAux = new Date(date.getTime() + appointment.duration * 60 * 1000);
        endTime = endTimeAux.getHours().toString().padStart(2,'0')
          +':'
          +endTimeAux.getMinutes().toString().padStart(2,'0');
      } 
    }
    return startTime + ' - ' + endTime;
  }

  editAppointment(appointment: Appointment){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    this.router.navigate(['/add-appointment'], {state: {appointment: appointment}});
  }

  getCurrencySymbol(code: string){
    const currency = TYPES.DATABASE.CURRENCY.find((c:any) => c.code === code);
    return currency ? currency.symbol : '';
  }

  attendance(appointmentId:string | null, isConfirmed: boolean){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    let status = this.STATUS.CONFIRMED.code;
    let message = this.translate.instant('info.appointment_confirmed');
    if(!isConfirmed){
     status = this.STATUS.REJECTED.code;
     message = this.translate.instant('info.appointment_rejected');
    }

    if(appointmentId){
      this.appointmentService.update(
        TYPES.DATABASE.TABLES.APPOINTMENTS,
        appointmentId,
        {
          status: status
        }
      );

      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);

      history.back();

    }
  }

  startSession(item: Appointment){
    if(item.meet_link){
      window.open(item.meet_link, '_blank');
    }
  }

  async deleteAppointment(appointment: Appointment){
    const appointmentId = appointment.id;
    const userId = this.authService.user()?.id;

    if(appointmentId && userId){

      const calendarId = appointment.calendar_id;
      if(calendarId){
        await this.edgeFunctionCallerService.googleCalendarEvent(
          {
            action: 'delete',
            userId : userId,
            title: '',
            startDateTime: '',
            endDateTime: '',
            timeZone: '',
            eventId: appointment.calendar_id,
            patientEmail: ''
          }
        );
      }

      await this.appointmentService.delete(
        TYPES.DATABASE.TABLES.APPOINTMENTS,
        appointmentId
      );

      const message = this.translate.instant('info.appointment_deleted');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);

      history.back();

    }
  }

}
